import { z } from 'zod';
import { USER_DEVICE_STATUS_VALUES } from '@fas/types';
import { nonEmptyTrimmed, paginationQuerySchema, uuidSchema } from './common';

// ── Device keys and proofs (§37, Phase 2B) ─────────────────────────────────────
/** 32 bytes, base64url without padding. */
const coordinate = z.string().regex(/^[A-Za-z0-9_-]{43}$/, 'Must be a 32-byte base64url value');

/** Public device key as a JWK (ECDSA P-256); only the four defining members are accepted. */
export const devicePublicJwkSchema = z
  .object({
    kty: z.literal('EC'),
    crv: z.literal('P-256'),
    x: coordinate,
    y: coordinate,
  })
  .strict();

export const deviceProofSchema = z
  .object({
    publicKey: devicePublicJwkSchema,
    challenge: z.string().min(1).max(300),
    /** 64-byte ECDSA signature (r‖s), base64url without padding. */
    signature: z.string().regex(/^[A-Za-z0-9_-]{86}$/, 'Must be a 64-byte base64url signature'),
  })
  .strict();
export type DeviceProofInput = z.infer<typeof deviceProofSchema>;

/**
 * Proof of a clock event recorded offline (Phase 2D): the signature covers the purpose, the
 * device's capture time and the request id — there is no server challenge to sign offline.
 */
export const offlineDeviceProofSchema = z
  .object({
    publicKey: devicePublicJwkSchema,
    signature: z.string().regex(/^[A-Za-z0-9_-]{86}$/, 'Must be a 64-byte base64url signature'),
  })
  .strict();
export type OfflineDeviceProofInput = z.infer<typeof offlineDeviceProofSchema>;

export const registerDeviceSchema = z
  .object({
    label: nonEmptyTrimmed(100, 'Device name'),
    proof: deviceProofSchema,
  })
  .strict();
export type RegisterDeviceInput = z.infer<typeof registerDeviceSchema>;

export const revokeDeviceSchema = z.object({ reason: nonEmptyTrimmed(500, 'Reason') }).strict();
export type RevokeDeviceInput = z.infer<typeof revokeDeviceSchema>;

export const listDevicesQuerySchema = paginationQuerySchema.extend({
  status: z.enum(USER_DEVICE_STATUS_VALUES).optional(),
  userId: uuidSchema.optional(),
});
export type ListDevicesQuery = z.infer<typeof listDevicesQuerySchema>;
